import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { getDateLocale } from '../utils/dateLocale';
import {
  ShoppingBag, Package, Euro, TrendingUp, FileText, Plus, Edit, Trash2,
  MessageSquare, Send, Loader2, X, ChevronRight, ChevronLeft, PieChart as PieChartIcon, BarChart3, Users, ArrowDownRight, Upload, Check, Search, ImagePlus, ClipboardList, Eye, Clock, CheckCircle,
  CreditCard, AlertTriangle, Crown, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import SafePaymentForm from '../components/SafePaymentForm';
import NotificationBell from '../components/NotificationBell';
import ProviderCatalogue from '../components/provider/ProviderCatalogue';
import ProviderFinances from '../components/provider/ProviderFinances';
import ProviderSales from '../components/provider/ProviderSales';
import ProviderSelectionsPanel from '../components/provider/ProviderSelectionsPanel';
import ToptexCatalog from '../components/provider/ToptexCatalog';
import ProviderMessages from '../components/provider/ProviderMessages';
// IdeesNatureImport removed
import HindbagImport from '../components/provider/HindbagImport';
import XDConnectsImport from '../components/provider/XDConnectsImport';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

const CHART_COLORS = ['#ff4500', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('catalogue');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', category: 'Textile', price: '', suggested_commission: 5, image_url: '', images: [], sizes: [], colors: [], stock: 100, pricing_tiers: [], brand: '', composition: '' });
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [organizerLogos, setOrganizerLogos] = useState([]);
  const [organizersList, setOrganizersList] = useState([]);
  const [financialData, setFinancialData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  // TopTex import
  const [importProducts, setImportProducts] = useState([]);
  const [importSelected, setImportSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState('');
  const [importFilter, setImportFilter] = useState('');
  // TopTex lookup by ref
  const [lookupRef, setLookupRef] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  // XDConnects lookup
  const [xdLookupRef, setXdLookupRef] = useState('');
  const [xdLookupLoading, setXdLookupLoading] = useState(false);
  const [xdLookupResult, setXdLookupResult] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [cardImageIndex, setCardImageIndex] = useState({});
  const imageInputRef = useRef(null);
  const [selections, setSelections] = useState([]);
  const [activeSelection, setActiveSelection] = useState(null);
  const [customizeImages, setCustomizeImages] = useState([]);
  const [uploadingCustomize, setUploadingCustomize] = useState(false);
  const customizeInputRef = useRef(null);
  const [subscription, setSubscription] = useState(null);
  const isMainPartner = true; // Moreati is the main provider
  // Boracay import
  const [boracayCategories, setBoracayCategories] = useState([]);
  const [boracayProducts, setBoracayProducts] = useState([]);
  const [boracayBrowsing, setBoracayBrowsing] = useState(false);
  const [boracayImporting, setBoracayImporting] = useState(null);
  const [boracayLookupUrl, setBoracayLookupUrl] = useState('');
  const [boracayLookupLoading, setBoracayLookupLoading] = useState(false);
  const [boracayLookupResult, setBoracayLookupResult] = useState(null);
  // Custom import request
  const [customImportMsg, setCustomImportMsg] = useState('');
  const [sendingImportRequest, setSendingImportRequest] = useState(false);
  // Inline payment dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const categories = ['Textile', 'Accessoire', 'Gourde', 'Sac', 'Nutrition', 'Équipement'];

  const fetchData = useCallback(async () => {
    try {
      const [catRes, ordRes, statsRes, convRes, logosRes, orgRes, finRes, salesRes, subRes] = await Promise.all([
        api.get('/provider/catalog'),
        api.get('/provider/orders'),
        api.get('/provider/stats'),
        api.get('/provider/conversations'),
        api.get('/provider/organizer-logos'),
        api.get('/provider/organizers'),
        api.get('/provider/financial-breakdown'),
        api.get('/provider/sales-breakdown'),
        api.get('/subscriptions/my').catch(() => ({ data: { subscription: null } }))
      ]);
      setProducts(catRes.data.products || []);
      setOrders(ordRes.data.orders || []);
      setStats(statsRes.data || {});
      setConversations(convRes.data.conversations || []);
      setOrganizerLogos(logosRes.data.organizers || []);
      setOrganizersList(orgRes.data.organizers || []);
      setFinancialData(finRes.data || null);
      setSalesData(salesRes.data || null);
      setSubscription(subRes.data.subscription || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) { toast.error('Nom et prix requis'); return; }
    const payload = {
      ...productForm,
      price: parseFloat(productForm.price),
      suggested_commission: parseFloat(productForm.suggested_commission),
      pricing_tiers: (productForm.pricing_tiers || []).map(t => ({
        min_qty: parseInt(t.min_qty) || 1,
        max_qty: t.max_qty ? parseInt(t.max_qty) : null,
        unit_price: parseFloat(t.unit_price) || 0,
        label: t.label || ''
      })).filter(t => t.unit_price > 0)
    };
    try {
      if (editingProduct) {
        await api.put(`/provider/catalog/${editingProduct.product_id}`, payload);
        toast.success('Produit modifié');
      } else {
        await api.post('/provider/catalog', payload);
        toast.success('Produit ajouté');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await api.delete(`/provider/catalog/${id}`);
    toast.success('Supprimé');
    fetchData();
  };

  const openChat = async (userId) => {
    setActiveChat(userId);
    const res = await api.get(`/provider/messages/${userId}`);
    setMessages(res.data.messages || []);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeChat) return;
    await api.post('/provider/messages', { recipient_id: activeChat, content: newMsg });
    setNewMsg('');
    const res = await api.get(`/provider/messages/${activeChat}`);
    setMessages(res.data.messages || []);
  };


  const handlePdfUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) { toast.error('Fichier PDF requis'); return; }
    setParsing(true);
    setParsingProgress('Upload du fichier...');
    setImportProducts([]);
    setImportSelected(new Set());
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/provider/import/parse-pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
      const taskId = uploadRes.data.task_id;
      if (!taskId) {
        setImportProducts(uploadRes.data.products || []);
        toast.success(`${uploadRes.data.total} produits trouvés !`);
        setParsing(false); setParsingProgress('');
        return;
      }
      setParsingProgress('Analyse du PDF...');
      let attempts = 0;
      const maxAttempts = 600;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 1000));
        attempts++;
        try {
          const statusRes = await api.get(`/provider/import/pdf-status/${taskId}`);
          if (statusRes.data.status === 'done') {
            setImportProducts(statusRes.data.products || []);
            toast.success(`${statusRes.data.total} produits trouvés dans le catalogue !`);
            setParsing(false); setParsingProgress('');
            return;
          }
          if (statusRes.data.total_pages > 0) {
            setParsingProgress(`Page ${statusRes.data.current_page} / ${statusRes.data.total_pages}`);
          }
        } catch (pollErr) {
          if (pollErr.response?.status === 500) {
            toast.error(pollErr.response?.data?.detail || 'Erreur analyse PDF');
            setParsing(false); setParsingProgress('');
            return;
          }
        }
      }
      toast.error('Le traitement a pris trop de temps.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload PDF'); }
    finally { setParsing(false); setParsingProgress(''); }
  };

  const handleImportConfirm = async () => {
    const selected = importProducts.filter(p => importSelected.has(p.ref));
    if (selected.length === 0) { toast.error('Sélectionnez au moins un produit'); return; }
    setImporting(true);
    try {
      const res = await api.post('/provider/import/confirm', { products: selected });
      toast.success(res.data.message);
      setImportProducts([]);
      setImportSelected(new Set());
      fetchData();
      setActiveSection('catalogue');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
    finally { setImporting(false); }
  };

  const toggleImportSelect = (ref) => {
    setImportSelected(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };

  const toggleImportAll = () => {
    const filtered = filteredImportProducts;
    if (importSelected.size === filtered.length) {
      setImportSelected(new Set());
    } else {
      setImportSelected(new Set(filtered.map(p => p.ref)));
    }
  };

  const filteredImportProducts = importProducts.filter(p =>
    !importFilter || p.name.toLowerCase().includes(importFilter.toLowerCase()) ||
    p.ref.toLowerCase().includes(importFilter.toLowerCase()) ||
    p.category.toLowerCase().includes(importFilter.toLowerCase())
  );

  const handleLookup = async () => {
    if (!lookupRef.trim()) { toast.error('Entrez une reference TopTex'); return; }
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const res = await api.get(`/provider/import/lookup/${lookupRef.trim()}`);
      setLookupResult(res.data.product);
      toast.success('Produit trouve !');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur recherche';
      toast.error(msg);
    } finally { setLookupLoading(false); }
  };

  const handleAddSingle = async () => {
    if (!lookupResult) return;
    try {
      await api.post('/provider/import/add-single', { product: lookupResult });
      toast.success(`${lookupResult.ref} ajouté au catalogue !`);
      setLookupResult(null);
      setLookupRef('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
  };

  // XDConnects handlers
  const handleXdLookup = async () => {
    if (!xdLookupRef.trim()) { toast.error('Entrez une reference XD Connects'); return; }
    setXdLookupLoading(true);
    setXdLookupResult(null);
    try {
      const res = await api.get(`/provider/import/xdconnects/lookup/${xdLookupRef.trim()}`);
      setXdLookupResult(res.data.product);
      toast.success('Produit trouve !');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur recherche';
      toast.error(msg);
    } finally { setXdLookupLoading(false); }
  };

  const handleXdAddSingle = async () => {
    if (!xdLookupResult) return;
    try {
      await api.post('/provider/import/xdconnects/add-single', { product: xdLookupResult });
      toast.success(`${xdLookupResult.ref} ajouté au catalogue !`);
      setXdLookupResult(null);
      setXdLookupRef('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    const currentImages = productForm.images || [];
    const remaining = 10 - currentImages.length;
    if (remaining <= 0) { toast.error('Maximum 10 images atteint'); return; }
    const filesToUpload = Array.from(files).slice(0, remaining);
    setUploadingImages(true);
    try {
      const uploaded = [];
      for (const file of filesToUpload) {
        const arrayBuffer = await file.arrayBuffer();
        const CHUNK_SIZE = 256 * 1024;
        const totalChunks = Math.ceil(arrayBuffer.byteLength / CHUNK_SIZE);
        const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        let res;
        for (let i = 0; i < totalChunks; i++) {
          const chunk = arrayBuffer.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          let binary = ''; for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
          const b64 = btoa(binary);
          res = await api.post('/upload/chunk', { filename: file.name, chunk_index: i, total_chunks: totalChunks, upload_id: uploadId, data: b64 }, { timeout: 30000 });
        }
        uploaded.push(res.data.url);
      }
      setProductForm(p => ({ ...p, images: [...(p.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image(s) ajoutée(s)`);
    } catch (e) { toast.error('Erreur upload image'); }
    finally { setUploadingImages(false); }
  };

  const removeFormImage = (index) => {
    setProductForm(p => ({ ...p, images: (p.images || []).filter((_, i) => i !== index) }));
  };

  const getProductImages = (p) => {
    const imgs = [];
    if (p.images && p.images.length > 0) imgs.push(...p.images);
    else if (p.image_url) imgs.push(p.image_url);
    return imgs.map(url => url ? url.replace(/ /g, '%20') : '').filter(Boolean);
  };

  const fetchSelections = useCallback(async () => {
    try {
      const res = await api.get('/provider/selections');
      setSelections(res.data.selections || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (activeSection === 'selections') fetchSelections();
  }, [activeSection, fetchSelections]);

  const handleCustomizeUpload = async (files, selectionId, productIndex) => {
    if (!files || files.length === 0) return;
    setUploadingCustomize(true);
    try {
      const uploaded = [...customizeImages];
      for (const file of Array.from(files).slice(0, 10 - uploaded.length)) {
        const arrayBuffer = await file.arrayBuffer();
        const CHUNK_SIZE = 256 * 1024;
        const totalChunks = Math.ceil(arrayBuffer.byteLength / CHUNK_SIZE);
        const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        let res;
        for (let i = 0; i < totalChunks; i++) {
          const chunk = arrayBuffer.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          let binary = ''; for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
          const b64 = btoa(binary);
          res = await api.post('/upload/chunk', { filename: file.name, chunk_index: i, total_chunks: totalChunks, upload_id: uploadId, data: b64 }, { timeout: 30000 });
        }
        uploaded.push(res.data.url);
      }
      setCustomizeImages(uploaded);
    } catch { toast.error('Erreur upload'); }
    finally { setUploadingCustomize(false); }
  };

  const handleSaveCustomization = async (selectionId, productIndex) => {
    if (customizeImages.length === 0) { toast.error('Ajoutez au moins une photo'); return; }
    try {
      await api.put(`/provider/selections/${selectionId}/customize/${productIndex}`, { images: customizeImages });
      toast.success('Produit personnalise ! Les photos sont synchronisees chez l\'organisateur.');
      setCustomizeImages([]);
      setActiveSelection(null);
      fetchSelections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSelectionStatus = async (selectionId, status) => {
    try {
      await api.put(`/provider/selections/${selectionId}/status`, { status });
      toast.success('Statut mis a jour');
      fetchSelections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Boracay import handlers
  const handleBoracayBrowse = async (categorySlug) => {
    setBoracayBrowsing(true);
    try {
      const res = await api.get(`/provider/import/boracay/browse/${categorySlug}`);
      setBoracayProducts(res.data.products || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur navigation Boracay'); }
    finally { setBoracayBrowsing(false); }
  };

  const handleBoracayImport = async (productUrl) => {
    setBoracayImporting(productUrl);
    try {
      const res = await api.post('/provider/import/boracay/import', { url: productUrl });
      toast.success(res.data.message || 'Produit importe !');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import Boracay'); }
    finally { setBoracayImporting(null); }
  };

  const handleBoracayLookup = async () => {
    if (!boracayLookupUrl.trim() || !boracayLookupUrl.includes('boracay.fr')) {
      toast.error('Entrez une URL Boracay valide'); return;
    }
    setBoracayLookupLoading(true);
    setBoracayLookupResult(null);
    try {
      const res = await api.get(`/provider/import/boracay/lookup?url=${encodeURIComponent(boracayLookupUrl.trim())}`);
      setBoracayLookupResult(res.data.product);
      toast.success('Produit trouve !');
    } catch (e) { toast.error(e.response?.data?.detail || 'Produit non trouve'); }
    finally { setBoracayLookupLoading(false); }
  };

  const handleBoracayAddFromLookup = async () => {
    if (!boracayLookupResult?.source_url) return;
    await handleBoracayImport(boracayLookupResult.source_url);
    setBoracayLookupResult(null);
    setBoracayLookupUrl('');
  };

  // Fetch Boracay categories
  useEffect(() => {
    if (activeSection === 'import-boracay' && isMainPartner && boracayCategories.length === 0) {
      api.get('/provider/import/boracay/categories').then(res => {
        setBoracayCategories(res.data.categories || []);
      }).catch(() => {});
    }
  }, [activeSection, isMainPartner, boracayCategories.length]);

  // Custom import request for non-main partners
  const handleRequestCustomImport = async () => {
    if (!customImportMsg.trim()) { toast.error('Decrivez votre besoin d\'import'); return; }
    setSendingImportRequest(true);
    try {
      await api.post('/provider/request-custom-import', { message: customImportMsg });
      toast.success('Demande envoyee a l\'administrateur !');
      setCustomImportMsg('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSendingImportRequest(false); }
  };

  // Inline subscription payment
  const handleSubscriptionPayment = async (token) => {
    setProcessingPayment(true);
    try {
      const res = await api.post('/subscriptions/pay-inline', {
        source_id: token.token
      });
      if (res.data.success) {
        setShowPaymentDialog(false);
        toast.success('Paiement reussi ! Votre abonnement est actif.');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    }
    setProcessingPayment(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="loader" /></div>;

  const statCards = [
    { label: 'Produits', value: stats.total_products || 0, icon: Package, color: 'text-violet-500' },
    { label: 'Commandes', value: stats.total_orders || 0, icon: FileText, color: 'text-blue-500' },
    { label: 'Ventes totales', value: `${(stats.total_sales || 0).toFixed(0)}€`, icon: Euro, color: 'text-green-600' },
    { label: 'Revenu net', value: `${(stats.net_revenue || 0).toFixed(0)}€`, icon: TrendingUp, color: 'text-brand' },
  ];

  const pendingSelections = selections.filter(s => s.status === 'pending').length;
  const baseNavItems = [
    { id: 'catalogue', label: 'Catalogue', icon: ShoppingBag },
    { id: 'selections', label: 'Selections', icon: ClipboardList, badge: pendingSelections },
  ];
  const importNavItems = isMainPartner ? [
    { id: 'import', label: 'Import TopTex', icon: Package },
    { id: 'import-xd', label: 'XD Connects', icon: Package },
    { id: 'import-boracay', label: 'Boracay', icon: Package },
    { id: 'import-hindbag', label: 'Hindbag', icon: Package },
  ] : [];
  const otherNavItems = [
    { id: 'finances', label: 'Finances', icon: Euro },
    { id: 'ventes', label: 'Ventes', icon: BarChart3 },
    { id: 'commandes', label: 'Commandes', icon: FileText },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: conversations.reduce((a, c) => a + (c.unread || 0), 0) },
  ];
  const navItems = [...baseNavItems, ...importNavItems, ...otherNavItems];

  const chatPartner = conversations.find(c => c.user_id === activeChat);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-dashboard">
      {/* Header - Branded */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl sm:text-2xl font-black uppercase tracking-tight text-white">Boutique {user?.company_name || 'Moreati'}</h1>
                <p className="text-xs text-slate-400">Gerez vos catalogues, importez des produits et suivez vos ventes</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2" onClick={() => { setEditingProduct(null); setProductForm({ name: '', description: '', category: 'Textile', price: '', suggested_commission: 5, image_url: '', images: [], sizes: [], colors: [], stock: 100, pricing_tiers: [], brand: '', composition: '' }); setShowProductDialog(true); }} data-testid="add-provider-product-btn">
              <Plus className="w-4 h-4" /> Nouveau produit
            </Button>
            <NotificationBell onNavigate={setActiveSection} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 p-4 hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="absolute -right-2 -top-2 w-16 h-16 bg-slate-50 rounded-full" />
              <s.icon className={`w-5 h-5 ${s.color} mb-2 relative z-10`} />
              <p className="font-heading font-black text-2xl relative z-10">{s.value}</p>
              <p className="text-xs text-slate-500 uppercase font-heading">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Subscription Banner */}
        {subscription && (
          <div className={`mb-6 p-4 border-l-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
            subscription.status === 'trial' ? 'bg-blue-50 border-blue-500' :
            subscription.status === 'active' ? 'bg-emerald-50 border-emerald-500' :
            subscription.status === 'trial_expired' || subscription.status === 'expired' ? 'bg-amber-50 border-amber-500' :
            'bg-slate-50 border-slate-400'
          }`} data-testid="subscription-banner">
            <div className="flex items-center gap-3">
              {subscription.status === 'trial' && <Clock className="w-5 h-5 text-blue-500" />}
              {subscription.status === 'active' && <Crown className="w-5 h-5 text-emerald-500" />}
              {(subscription.status === 'trial_expired' || subscription.status === 'expired') && <AlertTriangle className="w-5 h-5 text-amber-500" />}
              {subscription.status === 'cancelled' && <X className="w-5 h-5 text-slate-400" />}
              <div>
                {subscription.status === 'trial' && (() => {
                  const daysLeft = Math.max(0, Math.ceil((new Date(subscription.trial_end) - new Date()) / (1000 * 60 * 60 * 24)));
                  return (
                    <>
                      <p className="font-heading font-bold text-sm uppercase text-blue-800">Essai gratuit — {daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}</p>
                      <p className="text-xs text-blue-600">Profitez de toutes les fonctionnalites. Abonnez-vous a 19€/mois pour continuer apres l'essai.</p>
                    </>
                  );
                })()}
                {subscription.status === 'active' && (
                  <>
                    <p className="font-heading font-bold text-sm uppercase text-emerald-800">Abonnement actif — 19€/mois</p>
                    <p className="text-xs text-emerald-600">
                      Prochain renouvellement : {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('fr-FR') : '—'}
                      {' '} | {subscription.payments_made || 0}/{subscription.commitment_months || 12} mensualites payees
                    </p>
                  </>
                )}
                {(subscription.status === 'trial_expired' || subscription.status === 'expired') && (
                  <>
                    <p className="font-heading font-bold text-sm uppercase text-amber-800">
                      {subscription.status === 'trial_expired' ? 'Essai termine' : 'Abonnement expire'}
                    </p>
                    <p className="text-xs text-amber-600">Abonnez-vous pour continuer a utiliser la plateforme (19€/mois, engagement 12 mois).</p>
                  </>
                )}
                {subscription.status === 'cancelled' && (
                  <>
                    <p className="font-heading font-bold text-sm uppercase text-slate-600">Abonnement annule</p>
                    <p className="text-xs text-slate-500">Votre acces prendra fin a la fin de la periode en cours.</p>
                  </>
                )}
              </div>
            </div>
            {(subscription.status === 'trial' || subscription.status === 'trial_expired' || subscription.status === 'expired') && (
              <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2 shrink-0" onClick={() => setShowPaymentDialog(true)} data-testid="subscribe-btn">
                <CreditCard className="w-4 h-4" /> {subscription.status === 'trial' ? 'S\'abonner maintenant' : 'Renouveler'}
              </Button>
            )}
            {subscription.status === 'active' && subscription.payments_made < subscription.commitment_months && (
              <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2 shrink-0" onClick={() => setShowPaymentDialog(true)} data-testid="pay-next-month-btn">
                <CreditCard className="w-4 h-4" /> Payer le mois suivant
              </Button>
            )}
          </div>
        )}

        {/* Nav tabs — grouped */}
        <div className="mb-6">
          <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 items-center flex-wrap">
            {/* Catalogue group */}
            {navItems.filter(n => ['catalogue', 'selections'].includes(n.id)).map(n => (
              <Button key={n.id} variant={activeSection === n.id ? 'default' : 'outline'} size="sm" className={`gap-1.5 shrink-0 text-xs ${activeSection === n.id ? 'bg-slate-900 text-white' : ''}`} onClick={() => { setActiveSection(n.id); setActiveChat(null); }} data-testid={`provider-tab-${n.id}`}>
                <n.icon className="w-3.5 h-3.5" /> {n.label}
                {n.badge > 0 && <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center ml-1">{n.badge}</span>}
              </Button>
            ))}
            <span className="w-px h-6 bg-slate-200 mx-1 shrink-0" />
            {/* Import group */}
            {navItems.filter(n => n.id.startsWith('import')).map(n => {
              const colorMap = {
                'import': 'bg-blue-600', 'import-xd': 'bg-purple-600',
                'import-boracay': 'bg-amber-600',
                'import-hindbag': 'bg-rose-500'
              };
              return (
                <Button key={n.id} variant={activeSection === n.id ? 'default' : 'outline'} size="sm" className={`gap-1.5 shrink-0 text-xs ${activeSection === n.id ? (colorMap[n.id] || 'bg-brand') + ' text-white' : ''}`} onClick={() => { setActiveSection(n.id); setActiveChat(null); }} data-testid={`provider-tab-${n.id}`}>
                  <n.icon className="w-3.5 h-3.5" /> {n.label}
                </Button>
              );
            })}
            <span className="w-px h-6 bg-slate-200 mx-1 shrink-0" />
            {/* Business group */}
            {navItems.filter(n => !['catalogue', 'selections'].includes(n.id) && !n.id.startsWith('import')).map(n => (
              <Button key={n.id} variant={activeSection === n.id ? 'default' : 'outline'} size="sm" className={`gap-1.5 shrink-0 text-xs ${activeSection === n.id ? 'bg-brand text-white' : ''}`} onClick={() => { setActiveSection(n.id); setActiveChat(null); }} data-testid={`provider-tab-${n.id}`}>
                <n.icon className="w-3.5 h-3.5" /> {n.label}
                {n.badge > 0 && <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center ml-1">{n.badge}</span>}
              </Button>
            ))}
          </div>
        </div>

        {/* Catalogue */}
        {activeSection === 'catalogue' && (
          <ProviderCatalogue
            products={products}
            getProductImages={getProductImages}
            cardImageIndex={cardImageIndex}
            setCardImageIndex={setCardImageIndex}
            onEdit={(p) => { setEditingProduct(p); setProductForm({ name: p.name, description: p.description, category: p.category, price: p.price, suggested_commission: p.suggested_commission, image_url: p.image_url, images: p.images || [], sizes: p.sizes || [], colors: p.colors || [], stock: p.stock, pricing_tiers: p.pricing_tiers || [], brand: p.brand || '', composition: p.composition || '' }); setShowProductDialog(true); }}
            onDelete={handleDeleteProduct}
          />
        )}




        {/* ===== SELECTIONS (Workflow Organisateur) ===== */}
        {activeSection === 'selections' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="provider-selections-section">
            <ProviderSelectionsPanel selections={selections} fetchSelections={fetchSelections} handleCustomizeUpload={handleCustomizeUpload} uploadingImages={uploadingCustomize} />
          </motion.div>
        )}


        {/* ===== IMPORT TOPTEX ===== */}
        {activeSection === 'import' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="provider-import-section">
            <ToptexCatalog onImportDone={fetchData} />
          </motion.div>
        )}


        {/* ===== IMPORT XD CONNECTS ===== */}
        {activeSection === 'import-xd' && isMainPartner && (
          <XDConnectsImport onImported={fetchData} />
        )}


        {/* ===== IMPORT BORACAY ===== */}
        {activeSection === 'import-boracay' && isMainPartner && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="provider-import-boracay-section">
            <h3 className="font-heading font-bold text-base uppercase mb-4">Import Catalogue Boracay</h3>

            {/* Lookup by URL */}
            <div className="bg-white border border-slate-200 p-6 mb-6">
              <h4 className="font-heading font-bold uppercase text-xs mb-3">Importer par URL</h4>
              <p className="text-xs text-slate-500 mb-4">Collez l'URL d'un produit boracay.fr et le systeme recupere automatiquement toutes les informations.</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-[10px] font-heading uppercase text-slate-400">URL du produit Boracay</Label>
                  <Input value={boracayLookupUrl} onChange={(e) => setBoracayLookupUrl(e.target.value)} placeholder="https://www.boracay.fr/fr/..."
                    onKeyDown={(e) => e.key === 'Enter' && handleBoracayLookup()} className="text-sm h-10" data-testid="boracay-lookup-url-input" />
                </div>
                <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2 h-10" onClick={handleBoracayLookup} disabled={boracayLookupLoading} data-testid="boracay-lookup-btn">
                  {boracayLookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {boracayLookupLoading ? 'Recherche...' : 'Rechercher'}
                </Button>
              </div>

              {boracayLookupResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 border border-brand/30 bg-brand/5 p-4" data-testid="boracay-lookup-result">
                  <div className="flex gap-4">
                    {boracayLookupResult.image_url && (
                      <div className="w-32 h-32 bg-white border overflow-hidden flex-shrink-0">
                        <img src={boracayLookupResult.image_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display='none'; }} />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          {boracayLookupResult.ref && <span className="font-mono text-xs font-bold text-brand bg-brand/10 px-2 py-0.5">{boracayLookupResult.ref}</span>}
                          <span className="ml-2 text-xs font-bold text-slate-500">Boracay</span>
                        </div>
                      </div>
                      <h4 className="font-heading font-bold text-sm mt-1">{boracayLookupResult.name}</h4>
                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">{boracayLookupResult.description}</p>
                      {boracayLookupResult.material && <p className="text-[10px] text-slate-500 mt-1">Matiere: {boracayLookupResult.material}</p>}
                      {boracayLookupResult.dimensions && <p className="text-[10px] text-slate-500">Dimensions: {boracayLookupResult.dimensions}</p>}
                      <div className="flex items-center gap-3 mt-3">
                        <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2" onClick={handleBoracayAddFromLookup} disabled={boracayImporting} data-testid="boracay-add-lookup-btn">
                          {boracayImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Ajouter au catalogue
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs" onClick={() => setBoracayLookupResult(null)}>Annuler</Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Browse by category */}
            <div className="bg-white border border-slate-200 p-6">
              <h4 className="font-heading font-bold uppercase text-xs mb-3">Parcourir par categorie</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {boracayCategories.map(cat => (
                  <Button key={cat.slug} variant="outline" size="sm" className="text-xs" onClick={() => handleBoracayBrowse(cat.slug)} disabled={boracayBrowsing} data-testid={`boracay-cat-${cat.slug}`}>
                    {cat.label}
                  </Button>
                ))}
              </div>

              {boracayBrowsing && (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand" /><p className="text-xs text-slate-400 mt-2">Chargement des produits...</p></div>
              )}

              {!boracayBrowsing && boracayProducts.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                  {boracayProducts.map((prod, i) => (
                    <div key={i} className="border border-slate-200 overflow-hidden group" data-testid={`boracay-product-${i}`}>
                      <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                        {prod.image_url ? (
                          <img src={prod.image_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display='none'; }} />
                        ) : (
                          <Package className="w-8 h-8 text-slate-200" />
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-[11px] font-heading font-bold line-clamp-2 leading-tight">{prod.name}</p>
                        <Button size="sm" className="w-full mt-2 h-7 text-[10px] bg-brand hover:bg-brand/90 text-white gap-1" onClick={() => handleBoracayImport(prod.url)} disabled={boracayImporting === prod.url}>
                          {boracayImporting === prod.url ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Importer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}


        {/* ===== REQUEST CUSTOM IMPORT (non-main partners) ===== */}
        {activeSection === 'catalogue' && !isMainPartner && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 bg-white border border-slate-200 p-6" data-testid="custom-import-request-section">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-50 flex items-center justify-center flex-shrink-0 rounded">
                <Upload className="w-6 h-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-bold text-sm uppercase mb-1">Demander un import de catalogue personnalise</h4>
                <p className="text-xs text-slate-500 mb-3">Vous avez un catalogue fournisseur a importer ? Decrivez votre besoin et notre equipe s'en chargera.</p>
                <div className="flex gap-2">
                  <Input
                    value={customImportMsg}
                    onChange={(e) => setCustomImportMsg(e.target.value)}
                    placeholder="Ex: J'aimerais importer mon catalogue depuis mon-fournisseur.com..."
                    className="flex-1 text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleRequestCustomImport()}
                    data-testid="custom-import-message-input"
                  />
                  <Button className="bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-xs gap-2 shrink-0" onClick={handleRequestCustomImport} disabled={sendingImportRequest} data-testid="send-import-request-btn">
                    {sendingImportRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Envoyer
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}


        {/* ===== IMPORT HINDBAG ===== */}
        {activeSection === 'import-hindbag' && isMainPartner && (
          <HindbagImport onImported={fetchData} />
        )}


        {/* Finances */}
        {activeSection === 'finances' && financialData && (
          <ProviderFinances financialData={financialData} />
        )}

        {/* Ventes */}
        {activeSection === 'ventes' && salesData && (
          <ProviderSales salesData={salesData} />
        )}


        {/* Logos organisateurs */}
        {activeSection === 'logos' && (
          <div data-testid="logos-section">
            <div className="mb-4">
              <h3 className="font-heading font-bold text-base uppercase">Logos des organisateurs</h3>
              <p className="text-xs text-slate-500">Logos HD transmis par les organisateurs pour la personnalisation de leurs produits</p>
            </div>
            {organizerLogos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {organizerLogos.map(org => (
                  <div key={org.user_id} className="bg-white border border-slate-200 p-4" data-testid={`logo-${org.user_id}`}>
                    <div className="h-32 border border-slate-100 bg-slate-50 flex items-center justify-center mb-3 overflow-hidden">
                      <img src={org.logo_url} alt={org.name} className="max-w-full max-h-full object-contain" />
                    </div>
                    <h4 className="font-heading font-bold text-sm">{org.name}</h4>
                    <p className="text-xs text-slate-400">{org.email}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Reçu le {org.logo_uploaded_at && format(new Date(org.logo_uploaded_at), 'd MMM yyyy', { locale: getDateLocale() })}</p>
                    <a href={org.logo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand font-bold mt-2 hover:underline">
                      Télécharger HD <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 p-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-slate-400">Aucun logo reçu pour le moment. Les organisateurs doivent importer leur logo depuis leur espace Boutique.</p>
              </div>
            )}
          </div>
        )}

        {/* Orders */}
        {activeSection === 'commandes' && (
          <div className="bg-white border border-slate-200">
            <div className="p-4 border-b"><h3 className="font-heading font-bold uppercase text-sm">Commandes ({orders.length})</h3></div>
            {orders.length > 0 ? (
              <div className="divide-y">
                {orders.map(o => (
                  <div key={o.order_id} className="p-4 hover:bg-slate-50" data-testid={`provider-order-${o.order_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-brand font-bold">{o.order_id}</span>
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-green-100 text-green-700">{o.status}</span>
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${o.delivery_method === 'Livraison à domicile' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{o.delivery_method || 'Sur place'}</span>
                        </div>
                        <p className="font-heading font-bold text-sm mt-1">{o.user_name}</p>
                        <p className="text-xs text-slate-500">{o.user_email}</p>
                        {o.shipping_address && <p className="text-xs text-slate-400 mt-0.5">{o.shipping_address}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-bold text-lg">{o.total?.toFixed(2)}€</p>
                        <p className="text-[10px] text-slate-400 mt-1">{o.created_at && format(new Date(o.created_at), 'd MMM yyyy HH:mm', { locale: getDateLocale() })}</p>
                      </div>
                    </div>
                    <div className="mt-2 bg-slate-50 p-2">
                      {o.items?.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1">
                          <span className="font-medium">{item.product_name} {item.size && <span className="text-slate-400">Taille: {item.size}</span>} {item.color && <span className="text-slate-400">Couleur: {item.color}</span>} x{item.quantity}</span>
                          <span className="font-heading font-bold">{item.line_total?.toFixed(2)}€</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400">Aucune commande pour le moment</div>
            )}
          </div>
        )}

        {/* Messages */}
        {activeSection === 'messages' && (
          <ProviderMessages
            user={user}
            conversations={conversations}
            organizersList={organizersList}
            activeChat={activeChat}
            messages={messages}
            newMsg={newMsg}
            setNewMsg={setNewMsg}
            openChat={openChat}
            sendMessage={sendMessage}
          />
        )}
      </div>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={(open) => { setShowProductDialog(open); if (!open) setEditingProduct(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
            <DialogDescription className="sr-only">Formulaire produit prestataire</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-heading uppercase text-slate-500">Nom du produit *</Label><Input value={productForm.name} onChange={(e) => setProductForm(p => ({ ...p, name: e.target.value }))} data-testid="provider-product-name" /></div>
              <div><Label className="text-xs font-heading uppercase text-slate-500">Marque</Label><Input value={productForm.brand || ''} onChange={(e) => setProductForm(p => ({ ...p, brand: e.target.value }))} placeholder="Ex: PROACT, Kariban..." /></div>
            </div>
            <div><Label className="text-xs font-heading uppercase text-slate-500">Description</Label><textarea className="w-full border rounded-md p-2 text-sm min-h-[60px] resize-y" value={productForm.description} onChange={(e) => setProductForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><Label className="text-xs font-heading uppercase text-slate-500">Composition</Label><Input value={productForm.composition || ''} onChange={(e) => setProductForm(p => ({ ...p, composition: e.target.value }))} placeholder="Ex: 100% Polyester" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-heading uppercase text-slate-500">Categorie</Label>
                <Select value={productForm.category} onValueChange={(v) => setProductForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-heading uppercase text-slate-500">Prix unitaire (€) *</Label><Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm(p => ({ ...p, price: e.target.value }))} /></div>
              <div><Label className="text-xs font-heading uppercase text-slate-500">Stock</Label><Input type="number" value={productForm.stock} onChange={(e) => setProductForm(p => ({ ...p, stock: parseInt(e.target.value) || 0 }))} /></div>
            </div>

            {/* ===== PALIERS DE VENTE ===== */}
            <div className="border border-slate-200 p-4 bg-slate-50/50">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-heading uppercase text-slate-700 font-bold">Paliers de vente (prix degressifs)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-[10px] h-7 gap-1"
                  onClick={() => setProductForm(p => ({
                    ...p,
                    pricing_tiers: [...(p.pricing_tiers || []), { min_qty: '', max_qty: '', unit_price: '', label: '' }]
                  }))}
                  data-testid="add-pricing-tier-btn"
                >
                  <Plus className="w-3 h-3" /> Ajouter un palier
                </Button>
              </div>
              {(!productForm.pricing_tiers || productForm.pricing_tiers.length === 0) ? (
                <p className="text-[10px] text-slate-400 italic">Aucun palier defini. Le prix unitaire ci-dessus sera applique. Ajoutez des paliers pour des prix degressifs par quantite.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-[10px] font-heading uppercase text-slate-400 px-1">
                    <div className="col-span-2">Qte min</div>
                    <div className="col-span-2">Qte max</div>
                    <div className="col-span-3">Prix unit. (€)</div>
                    <div className="col-span-4">Label (optionnel)</div>
                    <div className="col-span-1"></div>
                  </div>
                  {productForm.pricing_tiers.map((tier, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`pricing-tier-${i}`}>
                      <Input
                        type="number"
                        min="1"
                        className="col-span-2 h-8 text-xs"
                        placeholder="10"
                        value={tier.min_qty}
                        onChange={(e) => {
                          const tiers = [...productForm.pricing_tiers];
                          tiers[i] = { ...tiers[i], min_qty: e.target.value };
                          setProductForm(p => ({ ...p, pricing_tiers: tiers }));
                        }}
                      />
                      <Input
                        type="number"
                        min="1"
                        className="col-span-2 h-8 text-xs"
                        placeholder="49"
                        value={tier.max_qty || ''}
                        onChange={(e) => {
                          const tiers = [...productForm.pricing_tiers];
                          tiers[i] = { ...tiers[i], max_qty: e.target.value };
                          setProductForm(p => ({ ...p, pricing_tiers: tiers }));
                        }}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        className="col-span-3 h-8 text-xs font-mono"
                        placeholder="8.50"
                        value={tier.unit_price}
                        onChange={(e) => {
                          const tiers = [...productForm.pricing_tiers];
                          tiers[i] = { ...tiers[i], unit_price: e.target.value };
                          setProductForm(p => ({ ...p, pricing_tiers: tiers }));
                        }}
                      />
                      <Input
                        className="col-span-4 h-8 text-xs"
                        placeholder="Ex: Lot club"
                        value={tier.label || ''}
                        onChange={(e) => {
                          const tiers = [...productForm.pricing_tiers];
                          tiers[i] = { ...tiers[i], label: e.target.value };
                          setProductForm(p => ({ ...p, pricing_tiers: tiers }));
                        }}
                      />
                      <button
                        className="col-span-1 w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        onClick={() => {
                          const tiers = productForm.pricing_tiers.filter((_, idx) => idx !== i);
                          setProductForm(p => ({ ...p, pricing_tiers: tiers }));
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <p className="text-[10px] text-slate-400 mt-1">Ex: 10-49 = 8€, 50-99 = 6.50€, 100+ = 5€. Laissez "Qte max" vide pour le dernier palier.</p>
                </div>
              )}
            </div>

            {/* Multi-image gallery upload */}
            <div>
              <Label className="text-xs font-heading uppercase text-slate-500 mb-2 block">Photos du produit ({(productForm.images || []).length}/10)</Label>
              {(productForm.images || []).length > 0 && (
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {(productForm.images || []).map((img, i) => (
                    <div key={i} className="relative aspect-square bg-slate-50 rounded-lg overflow-hidden border border-slate-200 group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeFormImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                      {i === 0 && <span className="absolute bottom-1 left-1 bg-brand text-white text-[8px] font-bold px-1 rounded">Principal</span>}
                    </div>
                  ))}
                </div>
              )}
              {(productForm.images || []).length < 10 && (
                <div className="flex gap-2">
                  <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(e.target.files)} />
                  <Button type="button" variant="outline" className="flex-1 h-10 text-xs gap-2 border-dashed" onClick={() => imageInputRef.current?.click()} disabled={uploadingImages}>
                    {uploadingImages ? <><Loader2 className="w-4 h-4 animate-spin" /> Upload...</> : <><ImagePlus className="w-4 h-4" /> Ajouter des photos</>}
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">Jusqu'a 10 photos. La premiere sera l'image principale.</p>
            </div>

            <div><Label className="text-xs font-heading uppercase text-slate-500">Ou URL image externe</Label><Input value={productForm.image_url} onChange={(e) => setProductForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." /></div>
            <div><Label className="text-xs font-heading uppercase text-slate-500">Tailles (separees par des virgules)</Label><Input value={(productForm.sizes || []).join(', ')} onChange={(e) => setProductForm(p => ({ ...p, sizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="S, M, L, XL" /></div>
            <div><Label className="text-xs font-heading uppercase text-slate-500">Couleurs (separees par des virgules)</Label><Input value={(productForm.colors || []).join(', ')} onChange={(e) => setProductForm(p => ({ ...p, colors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="Noir, Blanc, Bleu" /></div>
            <Button className="w-full bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase" onClick={handleSaveProduct} data-testid="save-provider-product">{editingProduct ? 'Enregistrer' : 'Ajouter au catalogue'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog - Inline Square Payment */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md" data-testid="subscription-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-lg uppercase">Paiement abonnement</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Abonnement mensuel partenaire iBookSport — <span className="font-bold text-brand">19,00 €/mois</span>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <SafePaymentForm
              applicationId={process.env.REACT_APP_SQUARE_APP_ID}
              locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
              cardTokenizeResponseReceived={handleSubscriptionPayment}
              createPaymentRequest={() => ({
                countryCode: 'FR',
                currencyCode: 'EUR',
                total: { amount: '19.00', label: 'Abonnement Partenaire' }
              })}
              buttonText="Payer 19,00 €"
              buttonCss={{
                backgroundColor: '#F97316',
                color: '#fff',
                fontFamily: 'Oswald, sans-serif',
                fontWeight: '700',
                textTransform: 'uppercase',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#EA580C' }
              }}
              processing={processingPayment}
            />
            <p className="text-[10px] text-slate-400 mt-4 text-center">Paiement securise par Square. Vos donnees bancaires ne sont jamais stockees sur nos serveurs.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderDashboard;
